import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Spinner } from "@kui-react/spinner";

import { Button } from "components/Graphs/Button";
import PublicPageShell from "components/PublicPageShell";
import InvitationAcceptedConfirmation from "components/InvitationAcceptedConfirmation";
import { LOCALSTORAGE_ACTIVE_ORG_ID_KEY } from "constants/index";
import { safeLocalStorageSetItem } from "utils/localStorage";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import { WorkspaceContext } from "contexts/WorkspaceContext";
import { useKas } from "contexts/KasContext";
import { getInvitationTokenStatus } from "./invitationTokens";
import { invitationClient } from "server/devplane/Invitations";
import { clearPostLoginRedirect, setPostLoginRedirect } from "utils/redirects";

interface RouteParams {
  orgId?: string;
}

const GENERIC_ACCEPT_ERROR =
  "Your invitation could not be accepted. It may have expired or already been used.";

function logAcceptInvitationError(error: unknown): void {
  console.error("Failed to accept invitation:", error);
}

function getTokenFromSearch(search: string): string | null {
  return new URLSearchParams(search).get("token");
}

function buildAcceptedPath(token: string, orgId: string): string {
  const params = new URLSearchParams({ token });
  return `/org/${encodeURIComponent(
    orgId,
  )}/accept-invitation/accepted?${params.toString()}`;
}

const AcceptInvitationGate: React.FC = () => {
  const { orgId: routeOrgId } = useParams<RouteParams>();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const token = getTokenFromSearch(location.search);

    if (!token) {
      clearPostLoginRedirect();
      history.replace("/");
      return;
    }

    const status = getInvitationTokenStatus(token);

    if (status === "ACCEPTED") {
      if (!routeOrgId) {
        clearPostLoginRedirect();
        history.replace("/");
        return;
      }

      const acceptedPath = buildAcceptedPath(token, routeOrgId);
      setPostLoginRedirect(acceptedPath);
      history.replace(acceptedPath);
      return;
    }

    if (status === "REJECTED") {
      clearPostLoginRedirect();
      history.replace("/accept-invitation/rejected");
      return;
    }

    clearPostLoginRedirect();
    history.replace("/");
  }, [history, location.search, routeOrgId]);

  return (
    <PublicPageShell>
      <div className="flex min-h-screen items-center justify-center px-4 py-16">
        <Spinner
          className="text-gray-700 dark:text-secondary"
          description="Checking invitation"
        />
      </div>
    </PublicPageShell>
  );
};

export const AcceptInvitationAcceptedPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { orgId: routeOrgId } = useParams<RouteParams>();
  const { refreshOrgAccesses, refreshOrgs, setActiveOrgId } =
    useContext(OrgContext);
  const { clear: clearEnvironments } = useContext(WorkspaceContext);
  const {
    refreshMe,
    isAuthenticated: isBrevUserAuthenticated,
    isLoading: isBrevUserLoading,
  } = useContext(UserContext);
  const [errorMessage, setErrorMessage] = useState("");
  const [joinedOrganization, setJoinedOrganization] = useState<{
    name?: string;
  } | null>(null);
  const invitationRequestRef = useRef<{
    key: string;
    promise: Promise<string | undefined>;
  } | null>(null);

  useEffect(() => {
    const token = getTokenFromSearch(location.search);
    let isMounted = true;

    const acceptInvitation = async () => {
      if (!token) {
        clearPostLoginRedirect();
        history.replace("/");
        return;
      }

      const orgId = routeOrgId;

      if (isBrevUserLoading || !isBrevUserAuthenticated) {
        return;
      }

      if (!orgId) {
        clearPostLoginRedirect();
        if (isMounted) {
          setErrorMessage(GENERIC_ACCEPT_ERROR);
        }
        return;
      }

      const invitationRequestKey = `${orgId}:${token}`;
      let acceptPromise =
        invitationRequestRef.current?.key === invitationRequestKey
          ? invitationRequestRef.current.promise
          : null;

      if (!acceptPromise) {
        acceptPromise = (async () => {
          await invitationClient.acceptInvitation({ token });
          clearPostLoginRedirect();
          safeLocalStorageSetItem(LOCALSTORAGE_ACTIVE_ORG_ID_KEY, orgId);
          const [organizations] = await Promise.all([
            refreshOrgs(),
            refreshMe(),
            refreshOrgAccesses(),
          ]);
          clearEnvironments();
          setActiveOrgId(orgId);
          return organizations.find((organization) => organization.id === orgId)
            ?.name;
        })();
        invitationRequestRef.current = {
          key: invitationRequestKey,
          promise: acceptPromise,
        };
      }

      try {
        const organizationName = await acceptPromise;
        if (isMounted) {
          setJoinedOrganization({ name: organizationName });
        }
      } catch (error) {
        logAcceptInvitationError(error);
        if (invitationRequestRef.current?.key === invitationRequestKey) {
          invitationRequestRef.current = null;
        }
        if (isMounted) {
          setErrorMessage(GENERIC_ACCEPT_ERROR);
        }
      }
    };

    acceptInvitation();

    return () => {
      isMounted = false;
    };
  }, [
    history,
    clearEnvironments,
    isBrevUserAuthenticated,
    isBrevUserLoading,
    location.search,
    refreshMe,
    refreshOrgAccesses,
    refreshOrgs,
    routeOrgId,
    setActiveOrgId,
  ]);

  const returnHome = () => {
    clearPostLoginRedirect();
    history.push("/org/environments");
  };

  const goToInvitedOrg = useCallback(() => {
    if (routeOrgId) {
      history.replace(`/org/${encodeURIComponent(routeOrgId)}/environments`);
    }
  }, [history, routeOrgId]);

  if (joinedOrganization) {
    return (
      <InvitationAcceptedConfirmation
        organizationName={joinedOrganization.name}
        onContinue={goToInvitedOrg}
      />
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-16">
      {errorMessage ? (
        <div className="max-w-lg text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Could not join organization
          </h1>
          <p className="mt-3 text-base text-gray-600 dark:text-secondary">
            {errorMessage}
          </p>
          <Button className="mt-6" variant="primary" onClick={returnHome}>
            Return to home
          </Button>
        </div>
      ) : (
        <Spinner
          className="text-gray-700 dark:text-secondary"
          description="Accepting invitation..."
        />
      )}
    </div>
  );
};

export const AcceptInvitationRejectedPage: React.FC = () => {
  const history = useHistory();
  const { isLoading, isUserLoggedIn } = useKas();
  const isLoggedIn = Boolean(isUserLoggedIn);

  const handleContinue = () => {
    if (isLoggedIn) {
      history.push("/org/environments");
      return;
    }

    clearPostLoginRedirect();
    history.push("/login");
  };

  return (
    <PublicPageShell>
      <div className="flex min-h-screen items-center justify-center px-4 py-16">
        <div className="max-w-lg text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Invitation rejected
          </h1>
          <p className="mt-3 text-base text-gray-600 dark:text-secondary">
            You rejected this organization invitation. No changes were made to
            your account.
          </p>
          <Button
            className="mt-6"
            variant="primary"
            disabled={isLoading}
            onClick={handleContinue}
          >
            {isLoggedIn ? "Go to home" : "Login or sign up"}
          </Button>
        </div>
      </div>
    </PublicPageShell>
  );
};

export default AcceptInvitationGate;
